/**
 * Settings panel — opens as a modal from the "אודות" tab in the account
 * drawer. Renders all user preferences with live toggles; saves on every
 * change via `settings.service.update`.
 */

import { get as getPrefs, update as updatePrefs } from './settings.service.js';
import { escape as esc } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('settings.view');

const OVERLAY_ID = 'settingsOverlay';

const AVATARS = ['🚗', '🚙', '🚐', '🏎️', '🚓', '🚕', '🛻', '🚜', '🛵', '🏍️', '🚲', '🧑‍✈️'];

/** Open the settings modal. Idempotent — re-opening replaces content.
 *  Kept for places that prefer modal UX (debug shortcuts, etc.). The
 *  account-drawer tab uses `renderInto()` instead — same content, no
 *  modal chrome. */
export function open() {
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  renderBody(overlay);
  overlay.classList.add('open');
}

export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
}

/**
 * Render the settings panel INLINE into the given host element (used by
 * the account-drawer "הגדרות" tab so the same content appears as a tab
 * page instead of a modal). Writes the same HTML the modal body uses
 * and wires up all event handlers.
 *
 * @param {string} hostId
 */
export function renderInto(hostId) {
  const host = document.getElementById(hostId);
  if (!host) {
    log.warn(`renderInto: #${hostId} not found`);
    return;
  }
  const p = getPrefs();
  host.innerHTML = `
    <div class="settings-inline">
      ${renderTheme(p)}
      ${renderAvatar(p)}
      ${renderNotif(p)}
      ${renderQuiet(p)}
      ${renderAbout()}
    </div>
  `;
  const body = host.querySelector('.settings-inline');
  if (!body) return;
  wireTheme(body);
  wireAvatar(body);
  wireNotif(body);
  wireQuiet(body);
}

// ====================================================================
// internals
// ====================================================================

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'settings-overlay';
  ov.innerHTML = `
    <aside class="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settingsTitle">
      <header class="settings-header">
        <h2 id="settingsTitle">⚙️ הגדרות</h2>
        <button type="button" class="settings-close" aria-label="סגור">✕</button>
      </header>
      <div class="settings-body"></div>
    </aside>
  `;
  ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  ov.querySelector('.settings-close')?.addEventListener('click', close);
  return ov;
}

function renderBody(overlay) {
  const body = overlay.querySelector('.settings-body');
  if (!body) return;
  const p = getPrefs();
  body.innerHTML = `
    ${renderTheme(p)}
    ${renderAvatar(p)}
    ${renderNotif(p)}
    ${renderQuiet(p)}
    ${renderAbout()}
  `;
  wireTheme(body);
  wireAvatar(body);
  wireNotif(body);
  wireQuiet(body);
}

// ---------- Theme ---------------------------------------------------

function renderTheme(p) {
  return `
    <section class="settings-section">
      <h3 class="settings-h3">🎨 ערכת נושא</h3>
      <div class="settings-segmented" data-group="theme">
        ${themeOption('light', '☀️ בהיר', p.theme === 'light')}
        ${themeOption('dark',  '🌙 כהה',  p.theme === 'dark')}
        ${themeOption('auto',  '⚙️ אוטומטי לפי המכשיר', p.theme === 'auto')}
      </div>
    </section>
  `;
}
function themeOption(value, label, active) {
  return `<button type="button" class="settings-pill ${active ? 'is-active' : ''}" data-theme-value="${value}">${esc(label)}</button>`;
}
function wireTheme(body) {
  body.querySelectorAll('[data-theme-value]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const value = btn.getAttribute('data-theme-value');
      if (!value) return;
      await updatePrefs({ theme: /** @type {any} */ (value) });
      body.querySelectorAll('[data-theme-value]').forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
    });
  });
}

// ---------- Avatar --------------------------------------------------

function renderAvatar(p) {
  const items = AVATARS.map((emoji) => `
    <button type="button" class="settings-avatar-btn ${p.avatar === emoji ? 'is-active' : ''}"
            data-avatar="${esc(emoji)}" aria-label="בחר ${esc(emoji)}">
      ${emoji}
    </button>
  `).join('');
  return `
    <section class="settings-section">
      <h3 class="settings-h3">🧑‍✈️ אווטאר</h3>
      <p class="settings-hint">בחר אייקון לפרופיל שלך — יוצג במקום אייקון הרכב הסכמטי.</p>
      <div class="settings-avatar-grid">${items}</div>
    </section>
  `;
}
function wireAvatar(body) {
  body.querySelectorAll('[data-avatar]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const emoji = btn.getAttribute('data-avatar') || '🚗';
      await updatePrefs({ avatar: emoji });
      body.querySelectorAll('[data-avatar]').forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
    });
  });
}

// ---------- Notifications -------------------------------------------

const NOTIF_ROWS = [
  { key: 'licenseExpiry', label: '🚗 חידוש רישיון רכב', hint: '30/14/3 ימים לפני פקיעת תוקף, ויום אחרי' },
  { key: 'recalls',       label: '🔧 קריאות יצרן',      hint: 'התראה כשהרכב כלול בהחזרת יצרן' },
  { key: 'ownership',     label: '📊 שינוי בעלות',      hint: 'כשמשרד התחבורה מציג בעלים אחר' },
  { key: 'chat',          label: '💬 הודעות מנהגים',    hint: 'כשמישהו שולח הודעה לרכב שלך' },
  { key: 'weather',       label: '🌧️ מזג אוויר קיצוני', hint: 'סופות, שלג, גלי חום צפויים' },
  { key: 'news',          label: '📰 דייג׳סט חדשות שבועי', hint: 'סיכום שבועי של חדשות הרכב' },
];

function renderNotif(p) {
  return `
    <section class="settings-section">
      <h3 class="settings-h3">🔔 התראות</h3>
      <p class="settings-hint">בחר אילו סוגי התראות לקבל. השינוי חל מיידית — אין צורך לרענן.</p>
      ${NOTIF_ROWS.map((r) => notifRow(r, p.notif[r.key])).join('')}
    </section>
  `;
}
function notifRow(row, checked) {
  return `
    <label class="settings-toggle-row">
      <div class="settings-toggle-text">
        <div class="settings-toggle-label">${esc(row.label)}</div>
        <div class="settings-toggle-hint">${esc(row.hint)}</div>
      </div>
      <input type="checkbox" class="settings-toggle" data-notif-key="${esc(row.key)}" ${checked ? 'checked' : ''} />
    </label>
  `;
}
function wireNotif(body) {
  body.querySelectorAll('[data-notif-key]').forEach((cb) => {
    cb.addEventListener('change', async () => {
      const key = cb.getAttribute('data-notif-key');
      if (!key) return;
      const checked = /** @type {HTMLInputElement} */ (cb).checked;
      const current = getPrefs().notif;
      await updatePrefs({ notif: { ...current, [key]: checked } });
    });
  });
}

// ---------- Quiet hours --------------------------------------------

function renderQuiet(p) {
  return `
    <section class="settings-section">
      <h3 class="settings-h3">🌙 שעות שקט</h3>
      <p class="settings-hint">בשעות אלו לא תקבל התראות (חוץ מהתראות חירום).</p>
      <label class="settings-toggle-row">
        <div class="settings-toggle-text">
          <div class="settings-toggle-label">הפעל שעות שקט</div>
        </div>
        <input type="checkbox" class="settings-toggle" id="quietEnabled" ${p.quiet.enabled ? 'checked' : ''} />
      </label>
      <div class="settings-quiet-range" ${p.quiet.enabled ? '' : 'aria-disabled="true"'}>
        <label>מ:
          <input type="time" id="quietFrom" value="${esc(p.quiet.from)}" ${p.quiet.enabled ? '' : 'disabled'} />
        </label>
        <label>עד:
          <input type="time" id="quietTo" value="${esc(p.quiet.to)}" ${p.quiet.enabled ? '' : 'disabled'} />
        </label>
      </div>
    </section>
  `;
}
function wireQuiet(body) {
  const enabled = /** @type {HTMLInputElement | null} */ (body.querySelector('#quietEnabled'));
  const fromIn  = /** @type {HTMLInputElement | null} */ (body.querySelector('#quietFrom'));
  const toIn    = /** @type {HTMLInputElement | null} */ (body.querySelector('#quietTo'));
  const range   = body.querySelector('.settings-quiet-range');
  if (!enabled || !fromIn || !toIn) return;

  const persist = async () => {
    await updatePrefs({ quiet: {
      enabled: enabled.checked,
      from: fromIn.value || '22:00',
      to:   toIn.value   || '07:00',
    } });
  };
  enabled.addEventListener('change', async () => {
    fromIn.disabled = !enabled.checked;
    toIn.disabled   = !enabled.checked;
    if (range) {
      if (enabled.checked) range.removeAttribute('aria-disabled');
      else range.setAttribute('aria-disabled', 'true');
    }
    await persist();
  });
  fromIn.addEventListener('change', persist);
  toIn.addEventListener('change', persist);
}

// ---------- About / build version -----------------------------------

function renderAbout() {
  return `
    <section class="settings-section">
      <h3 class="settings-h3">ℹ️ אודות</h3>
      <div class="settings-about">
        <div>גרסה: <span data-build-version-mirror>—</span></div>
        <div>פיתוח: AutoMe™</div>
        <div><a href="/privacy.html" target="_blank" rel="noopener">מדיניות פרטיות</a></div>
        <div><a href="/contact.html" target="_blank" rel="noopener">יצירת קשר</a></div>
      </div>
    </section>
  `;
}
